import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Tones tuned for the DARK brand-primary sidebar background.
const STATUS_TONE = {
  ok:              { dot: "bg-sage", text: "text-sage", label: "All systems healthy" },
  warning:         { dot: "bg-gold/80", text: "text-gold/90", label: "Limited (known)" },
  degraded:        { dot: "bg-gold", text: "text-gold", label: "Degraded" },
  down:            { dot: "bg-terracotta", text: "text-terracotta", label: "Issue detected" },
  not_configured:  { dot: "bg-white/30", text: "text-white/60", label: "Setup pending" },
};

/** Tiny status pill that lives at the bottom of the sidebar.
 *  Only rendered for admins. Polls /api/system/health every 60s.
 *  Click → /settings (Integrations tab — System Health panel). */
export default function SidebarSystemStatus() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data: res } = await api.get("/system/health");
        if (!cancelled) setData(res);
      } catch {
        // silent
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.role]);

  if (user?.role !== "admin") return null;
  const tone = STATUS_TONE[data?.overall] || STATUS_TONE.not_configured;
  const label = data ? tone.label : "Checking system…";

  const services = data?.services || {};
  // Warnings (e.g., Pushpay Scheduler scope blocker) are documented limitations,
  // not failures — they should not count as "issues" in the sidebar badge.
  const issues = Object.values(services).filter((s) => s.status === "down" || s.status === "degraded").length;

  return (
    <Link
      to="/settings"
      data-testid="sidebar-system-status"
      className="flex items-center gap-2.5 px-3 py-2 mb-2 rounded-xl hover:bg-white/5 transition-colors"
      title={`${label} · click to view details`}
    >
      <span className="relative flex shrink-0">
        <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
        {data?.overall === "ok" && (
          <span className={`absolute inset-0 rounded-full ${tone.dot} opacity-60 animate-ping`} />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-medium ${tone.text} truncate`} data-testid="sidebar-system-status-label">
          {label}
        </p>
        {issues > 0 && (
          <p className="text-[10px] text-white/50 truncate">
            {issues} {issues === 1 ? "issue" : "issues"} · view details
          </p>
        )}
      </div>
    </Link>
  );
}
